import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useI18n } from 'core/i18n/I18nProvider';
import { IconSettings } from 'shared/components/Icons';

export function detailsAction(to, t) {
  return { key: 'details', label: t('actions.details'), to };
}

export function RowActionsHeader({ label }) {
  const { t } = useI18n();
  return (
    <th className="row-actions-col">
      <span className="sr-only">{label || t('actions.rowActions')}</span>
    </th>
  );
}

export function RowActionsCell({ items, label }) {
  return (
    <td className="table-actions row-actions-col">
      <RowActionsMenu items={items} label={label} />
    </td>
  );
}

function visibleItems(items) {
  return (items || []).filter((item) => item && !item.hidden);
}

export function RowActionsMenu({ items, label }) {
  const { t, dir } = useI18n();
  const menuId = useId();
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, ready: false });
  const actions = useMemo(() => visibleItems(items), [items]);
  const triggerLabel = label || t('actions.rowActions');

  const updatePosition = useCallback(() => {
    const button = buttonRef.current;
    const menu = menuRef.current;
    if (!button) {
      return;
    }
    const rect = button.getBoundingClientRect();
    const menuWidth = menu?.offsetWidth || 180;
    const menuHeight = menu?.offsetHeight || 8;
    const gap = 4;
    const viewportPadding = 8;
    const spaceBelow = window.innerHeight - rect.bottom - viewportPadding;
    const openUp = spaceBelow < menuHeight && rect.top > spaceBelow;
    const top = openUp
      ? Math.max(viewportPadding, rect.top - menuHeight - gap)
      : Math.min(window.innerHeight - menuHeight - viewportPadding, rect.bottom + gap);
    let left = dir === 'rtl' ? rect.left : rect.right - menuWidth;
    left = Math.min(Math.max(viewportPadding, left), window.innerWidth - menuWidth - viewportPadding);
    setCoords({ top, left, ready: true });
  }, [dir]);

  useLayoutEffect(() => {
    if (!open) {
      setCoords((current) => (current.ready ? { top: 0, left: 0, ready: false } : current));
      return undefined;
    }
    updatePosition();
    const onReposition = () => updatePosition();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open, updatePosition, actions.length]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onPointerDown = (event) => {
      if (buttonRef.current?.contains(event.target) || menuRef.current?.contains(event.target)) {
        return;
      }
      setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  if (actions.length === 0) {
    return null;
  }

  const close = () => setOpen(false);

  return (
    <div className="row-actions">
      <button
        ref={buttonRef}
        type="button"
        className="icon-btn row-actions-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={triggerLabel}
        onClick={() => setOpen((current) => !current)}
      >
        <IconSettings />
      </button>
      {open ? createPortal(
        <div
          ref={menuRef}
          id={menuId}
          role="menu"
          aria-label={triggerLabel}
          className="row-actions-menu"
          style={{ top: coords.top, left: coords.left, visibility: coords.ready ? 'visible' : 'hidden' }}
        >
          {actions.map((item) => {
            const className = ['row-actions-item', item.tone === 'danger' ? 'is-danger' : ''].filter(Boolean).join(' ');
            if (item.to && !item.disabled) {
              return (
                <Link
                  key={item.key || item.to}
                  role="menuitem"
                  className={className}
                  to={item.to}
                  onClick={close}
                >
                  {item.label}
                </Link>
              );
            }
            return (
              <button
                key={item.key || item.label}
                type="button"
                role="menuitem"
                className={className}
                aria-disabled={item.disabled || undefined}
                onClick={() => {
                  if (item.disabled) {
                    return;
                  }
                  close();
                  item.onClick?.();
                }}
              >
                {item.label}
              </button>
            );
          })}
        </div>,
        document.body,
      ) : null}
    </div>
  );
}
